import { useContext, useMemo, useState } from 'react';
import MantineTable from '../../../../common/table/mantine';
import { CostingRequestsContext } from '../../../../contexts/CostingRequest';
import { Alert, Button, Grid } from '@mantine/core';
import { CountrySelect, TableHeaderContent } from '../../styles';
import withConfirm from '../../../../components/common/confirm-dialog';
import { AppLoader } from '../../../../components/common/loader';

export const plantColumns = (type, isTransferPricing = false) => {
  if (isTransferPricing || type === 'Buying') {
    return [
      {
        id: 'Country',
        header: `${type} Country`,
        accessorFn: (row) =>
          row.Country || row.producing_country || row.selling_country || row.buying_country || '',
      },
      {
        id: 'Zone',
        header: `${type} Zone`,
        accessorFn: (row) =>
          row.Zone || row.producing_zone || row.selling_zone || row.buying_zone || '',
      },
    ];
  } else {
    return [
      {
        id: 'Plant',
        header: `${type} Plants`,
        accessorFn: (row) =>
          row.Plant || row.producing_plant || row.selling_plant || row.buying_plant || '',
      },
      {
        id: 'Country',
        header: `${type} Country`,
        accessorFn: (row) =>
          row.Country || row.producing_country || row.selling_country || row.buying_country || '',
      },
      {
        id: 'Zone',
        header: `${type} Zone`,
        accessorFn: (row) =>
          row.Zone || row.producing_zone || row.selling_zone || row.buying_zone || '',
      },
    ];
  }
};

const PlantsTable = ({
  title,
  onSubmit,
  info,
  confirm,
  loading = false,
  limit,
  selectedValues,
  editMode = false,
}) => {
  const { plants, countries } = useContext(CostingRequestsContext);
  const [activeMaterials, setActiveMaterials] = useState(info?.active || {});
  const [selectedCountry, setSelectedCountry] = useState(info.country || null);
  const isTransferPricing = selectedValues?.type_of_request === 'Transfer Pricing';
  const isPlantSelection = ['Select Production Details', 'Select Selling Plant'].includes(title);
  const gridSpan = isPlantSelection ? 10 : 12;

  const columns = useMemo(
    () => plantColumns(title.split(' ')[1], isTransferPricing),
    [plants, isTransferPricing],
  );
  const handleRowSelection = (data) => {
    let currentValues = { ...activeMaterials };

    currentValues = { ...data };
    setActiveMaterials(currentValues);
  };

  const limitExceeded = () => {
    return Object.keys(activeMaterials).length > limit;
  };

  const getBuyingPlants = () => {
    const producingCountry =
      title !== 'Select Production Details'
        ? selectedValues?.producing_plant_data?.length
          ? selectedValues.producing_plant_data[0].producing_country
          : null
        : null;

    return Array.from(
      new Map(
        plants
          .filter((plant) => !producingCountry || plant.Country !== producingCountry)
          .map((plant) => [`${plant.Country}-${plant.Zone}`, { ...plant, Plant: null }]),
      ).values(),
    );
  };
  const getInitialPlantData = ({ title, isTransferPricing, selectedCountry, editMode }) => {
    if (title === 'Select Production Details' && !isTransferPricing) {
      return selectedCountry ? plants.filter((plant) => plant.Country === selectedCountry) : [];
    }

    return getBuyingPlants();
  };

  const getProducingCountry = (countries) => {
    const { type_of_request } = selectedValues || {};
    const buyingCountry = selectedValues?.buying_plant_data?.length
      ? selectedValues.buying_plant_data[0].buying_country
      : null;

    return buyingCountry && type_of_request && type_of_request !== 'Costing'
      ? countries.filter((country) => (country || '').toLowerCase() !== buyingCountry.toLowerCase())
      : countries;
  };

  return (
    <>
      <TableHeaderContent>
        <Grid>
          {isPlantSelection && !isTransferPricing && (
            <Grid.Col span={3}>
              <CountrySelect
                searchable
                value={selectedCountry}
                placeholder='Select a country'
                data={getProducingCountry(countries)}
                onChange={(value) => {
                  if (value !== selectedCountry && selectedCountry) {
                    return confirm(
                      () => {
                        setActiveMaterials({});
                        setSelectedCountry(value);
                      },
                      {
                        title: 'Confirm!',
                        description:
                          'Are you sure you want to change the country? This action will remove all selected plants.',

                        confirmationText: 'Confirm',
                      },
                    );
                  } else {
                    setSelectedCountry(value);
                  }
                }}
                clearable
              />
            </Grid.Col>
          )}
          <Grid.Col span={gridSpan}>
            {limitExceeded() && (
              <Alert title='' color='red' className='mb-2'>
                {`Only ${limit} selections are allowed`}
              </Alert>
            )}
          </Grid.Col>
        </Grid>
      </TableHeaderContent>

      <MantineTable
        columns={columns}
        initialData={getInitialPlantData({
          title,
          isTransferPricing,
          selectedCountry,
          editMode,
          plants,
        })}
        unique={isTransferPricing ? 'Country' : 'Plant'}
        handleRowSelection={handleRowSelection}
        activeRows={activeMaterials}
        enableRowSelection={true}
        loading={false}
        showSelectedToggle={true}
        hideSelectColumn={false}
        showResetAll={false}
        fullScreen={false}
        multiRowSelection={['Select Buying Details'].includes(title) ? true : false}
      />

      <Grid justify='flex-end' style={{ marginTop: '16px' }}>
        <Grid.Col span={2}>
          <Button
            color='dark'
            disabled={limitExceeded()}
            fullWidth
            onClick={() => onSubmit(activeMaterials, title)}
          >
            {loading ? <AppLoader size='sm' variant='dark' /> : 'Confirm'}
          </Button>
        </Grid.Col>
      </Grid>
    </>
  );
};

export default withConfirm(PlantsTable);
